import { useEffect, useRef, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { BASE_URL } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { useToasts } from "@/hooks/useToast";
import type { LiveEvent } from "@/types";

export type LiveState = "connecting" | "live" | "offline";

/** One server-sent-event block ("event: …\ndata: …") as a typed event; comments and junk give null. */
export function parseSseBlock(block: string): LiveEvent | null {
  let type: string | null = null;
  const data: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith(":")) continue;
    if (line.startsWith("event:")) type = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
  }
  if (!data.length) return null;
  try {
    const parsed = JSON.parse(data.join("\n"));
    return { ...parsed, type: parsed.type ?? type } as LiveEvent;
  } catch {
    return null;
  }
}

/** Refresh whatever the event made stale. */
export function applyLiveEvent(qc: QueryClient, event: LiveEvent): void {
  switch (event.type) {
    case "audit.updated":
    case "flag.updated":
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["audit", "report", event.appointment_id] });
      qc.invalidateQueries({ queryKey: ["discrepancies"] });
      break;
    case "sync.completed":
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["integrations", "ezyvet"] });
      qc.invalidateQueries({ queryKey: ["ezyvet"] });
      break;
    case "integration.error":
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["integrations", "ezyvet"] });
      break;
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Keeps a connection to /api/v1/events/stream while signed in, so new audits,
 * flag changes and connection problems show up without a reload. Uses fetch
 * (not EventSource) so the request can carry the Authorization header.
 * Reconnects with backoff; toasts are batched so a backfill doesn't flood the screen.
 */
export function useLiveEvents(): LiveState {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const push = useToasts((s) => s.push);
  const [state, setState] = useState<LiveState>("connecting");
  const pending = useRef<{ flagged: number; names: string[]; timer?: number }>({ flagged: 0, names: [] });

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    let stopped = false;

    const announce = (event: LiveEvent) => {
      if (event.type !== "audit.updated" || event.missing_charges === 0) return;
      const p = pending.current;
      p.flagged += 1;
      if (event.patient_name) p.names.push(event.patient_name);
      window.clearTimeout(p.timer);
      p.timer = window.setTimeout(() => {
        const who = p.names.length === 1 ? `${p.names[0]}'s consult has` : `${p.flagged} consults have`;
        push(`${who} possible missed charges.`, "info");
        pending.current = { flagged: 0, names: [] };
      }, 2500);
    };

    const run = async () => {
      let backoff = 1000;
      while (!stopped) {
        try {
          setState("connecting");
          const res = await fetch(`${BASE_URL}/api/v1/events/stream`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
            signal: controller.signal,
          });
          if (res.status === 401) {
            useAuthStore.getState().clear();
            return;
          }
          if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
          setState("live");
          backoff = 1000;

          const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
          let buffer = "";
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += value.replace(/\r\n/g, "\n");
            let end: number;
            while ((end = buffer.indexOf("\n\n")) >= 0) {
              const event = parseSseBlock(buffer.slice(0, end));
              buffer = buffer.slice(end + 2);
              if (event) {
                applyLiveEvent(qc, event);
                announce(event);
              }
            }
          }
        } catch {
          if (controller.signal.aborted) return;
        }
        setState("offline");
        await sleep(backoff);
        backoff = Math.min(backoff * 2, 30_000);
      }
    };

    void run();
    return () => {
      stopped = true;
      controller.abort();
      window.clearTimeout(pending.current.timer);
    };
  }, [token, qc, push]);

  return state;
}
